import { differenceInCalendarDays, format, isSameMonth, parseISO } from 'date-fns';
import { enGB, fr } from 'date-fns/locale';
import { getLocale, getT, ns, type Locale } from '@/i18n';
import type { Currency } from '@/services/domain';

/*
 * Display formats per language (P12, Decision 94). French is the reference and unchanged: «1 196 MAD»,
 * «ven. 13 nov.», «13 → 17 nov.», «4 jours». English is British: «1,196 MAD», «€110», «$120», «Fri 13 Nov»,
 * «13 → 17 Nov», «4 days».
 *
 * Every function takes the language last and defaults to the current one (`getLocale()`), so non-React code
 * (services mappers) formats in the current language. Components use `useFormat()` (app/state/preferences),
 * which re-renders them when the language or the currency changes.
 */

/**
 * Indicative rates from MAD: only the default of `formatMoney` for non-React code and tests. The interface uses
 * the live rates (`GET /api/currency/rates`) and shows MAD when one is missing (API-FX-1), never these.
 */
export const DEFAULT_RATES: Record<Currency, number> = { MAD: 1, EUR: 0.092, USD: 0.1 };

/** Rates as the config gives them: a missing, null or invalid rate cannot price that currency. */
export type Rates = Partial<Record<Currency, number | null>>;

const usableRate = (r: unknown): r is number => typeof r === 'number' && Number.isFinite(r) && r > 0;

/**
 * The currency prices are shown and charged in (API-FX-1, the old site's `resolveEffectiveCurrency`): MAD stays
 * MAD; while the rates load the choice is kept; once loaded (or failed), a missing or invalid rate means MAD.
 * The stored choice itself is not changed.
 */
export function effectiveCurrency(chosen: Currency, rates: Rates | null | undefined, loading = false): Currency {
  if (chosen === 'MAD') return 'MAD';
  if (loading) return chosen;
  return usableRate(rates?.[chosen]) ? chosen : 'MAD';
}

const DATE_LOCALES = { fr, en: enGB } as const;
const dateLocale = (locale: Locale) => ({ locale: DATE_LOCALES[locale] });

/** French groups thousands with a narrow no-break space, English with a comma; the unit stays attached (no-break space). */
const NNBSP = ' ';
const NBSP = ' ';
const group = (digits: string, locale: Locale) =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, locale === 'en' ? ',' : NNBSP);
const groupThousands = (n: number, locale: Locale) => {
  const r = Math.round(n);
  return `${r < 0 ? '-' : ''}${group(String(Math.abs(r)), locale)}`;
};

/**
 * fr «1 196 MAD», «110 €», «120 $»; en «1,196 MAD», «€110», «$120» (MAD after the number, as the legacy site and
 * local usage write it). Amounts are stored in MAD. Without a usable rate for `currency` (missing, null, still
 * loading) the amount is shown in MAD: the display never differs from what can be charged (legacy `formatPrice`).
 */
export function formatMoney(
  amountMad: number,
  currency: Currency = 'MAD',
  rates: Rates = DEFAULT_RATES,
  locale: Locale = getLocale(),
): string {
  const rate = rates[currency];
  if (currency !== 'MAD' && !usableRate(rate)) return formatMoney(amountMad, 'MAD', rates, locale);
  const v = groupThousands(amountMad * (currency === 'MAD' ? 1 : (rate as number)), locale);
  if (locale === 'en') {
    if (currency === 'EUR') return `€${v}`;
    if (currency === 'USD') return `$${v}`;
    return `${v}${NBSP}MAD`;
  }
  if (currency === 'EUR') return `${v}${NBSP}€`;
  if (currency === 'USD') return `${v}${NBSP}$`;
  return `${v}${NBSP}MAD`;
}

/**
 * The amount Stripe pre-authorises, in the currency of the intent (Decision 9): EUR and USD with 2 decimals,
 * MAD whole unless the server amount has cents (a coupon discount can give some).
 * fr «1 196,50 MAD», «110,40 €»; en «1,196.50 MAD», «€110.40».
 */
export function formatCharge(amount: number, currency: string = 'MAD', locale: Locale = getLocale()): string {
  const cur = currency.toUpperCase();
  const cents = Math.round(amount * 100);
  const decimals = cur === 'MAD' && cents % 100 === 0 ? 0 : 2;
  const [int = '0', dec] = (cents / 100).toFixed(decimals).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, locale === 'en' ? ',' : NNBSP);
  const value = dec ? `${grouped}${locale === 'en' ? '.' : ','}${dec}` : grouped;
  if (locale === 'en') {
    if (cur === 'EUR') return `€${value}`;
    if (cur === 'USD') return `$${value}`;
    return `${value}${NBSP}${cur}`;
  }
  if (cur === 'EUR') return `${value}${NBSP}€`;
  if (cur === 'USD') return `${value}${NBSP}$`;
  return `${value}${NBSP}${cur}`;
}

/** fr «4,8», en «4.8» (ratings, decimals). */
export function formatNumber(n: number, maxDecimals = 1, locale: Locale = getLocale()): string {
  return n.toLocaleString(locale === 'en' ? 'en-GB' : 'fr-FR', { maximumFractionDigits: maxDecimals });
}

/** A fixed number of decimals: fr «4,0», en «4.0» (ratings «4,0 / 5»). */
export function formatFixed(n: number, digits: number, locale: Locale = getLocale()): string {
  const v = n.toFixed(digits);
  return locale === 'en' ? v : v.replace('.', ',');
}

/** fr «10 %», en «10%». */
export function formatPercent(n: number, locale: Locale = getLocale()): string {
  return locale === 'en' ? `${formatNumber(n, 2, locale)}%` : `${formatNumber(n, 2, locale)}${NBSP}%`;
}

const toDate = (d: string | Date) => (typeof d === 'string' ? parseISO(d) : d);

/** fr «ven. 13 nov.», en «Fri 13 Nov» */
export function formatDayShort(iso: string | Date, locale: Locale = getLocale()): string {
  return format(toDate(iso), 'EEE d MMM', dateLocale(locale));
}

/** fr «13 nov.», en «13 Nov» */
export function formatDayMonth(iso: string | Date, locale: Locale = getLocale()): string {
  return format(toDate(iso), 'd MMM', dateLocale(locale));
}

/** fr «vendredi 13 novembre», en «Friday 13 November» */
export function formatDayLong(iso: string | Date, locale: Locale = getLocale()): string {
  return format(toDate(iso), 'EEEE d MMMM', dateLocale(locale));
}

/** fr «13 novembre 2026», en «13 November 2026» */
export function formatDate(iso: string | Date, locale: Locale = getLocale()): string {
  return format(toDate(iso), 'd MMMM yyyy', dateLocale(locale));
}

/** fr «novembre 2026», en «November 2026» */
export function formatMonthYear(iso: string | Date, locale: Locale = getLocale()): string {
  return format(toDate(iso), 'LLLL yyyy', dateLocale(locale));
}

/** fr «ven.», en «Fri» */
export function formatWeekdayShort(iso: string | Date, locale: Locale = getLocale()): string {
  return format(toDate(iso), 'EEE', dateLocale(locale));
}

/** fr «ven. 13», en «Fri 13» */
export function formatWeekdayDay(iso: string | Date, locale: Locale = getLocale()): string {
  return format(toDate(iso), 'EEE d', dateLocale(locale));
}

/** fr «L», en «M» (calendar column heads) */
export function formatWeekdayNarrow(iso: string | Date, locale: Locale = getLocale()): string {
  return format(toDate(iso), 'EEEEE', dateLocale(locale));
}

/** fr «ven. 13 nov. à 10:00», en «Fri 13 Nov at 10:00» */
export function formatDayTime(iso: string | Date, time: string, locale: Locale = getLocale()): string {
  return getT(ns.format, locale)('dayAtTime', { day: formatDayShort(iso, locale), time });
}

/** fr «13 → 17 nov.» or «30 oct. → 2 nov.»; «13 nov. → ?» while the end is missing (en «13 → 17 Nov»). */
export function formatRange(start: string, end: string | null, locale: Locale = getLocale()): string {
  const s = parseISO(start);
  const l = dateLocale(locale);
  if (!end) return `${format(s, 'd MMM', l)} → ?`;
  const e = parseISO(end);
  if (isSameMonth(s, e)) return `${format(s, 'd')} → ${format(e, 'd MMM', l)}`;
  return `${format(s, 'd MMM', l)} → ${format(e, 'd MMM', l)}`;
}

/** fr «1 jour», «4 jours»; en «1 day», «4 days» */
export const formatDays = (n: number, locale: Locale = getLocale()): string =>
  getT(ns.format, locale)('days', { count: n });

/** Calendar nights between two ISO dates. */
export const nightsBetween = (start: string, end: string): number =>
  differenceInCalendarDays(parseISO(end), parseISO(start));
